import { ReactNode, useState } from 'react';
import { ReactComponent as ChevronDown } from '@assets/icons/chevron-down.svg';
import { inputDateFormat } from 'constant';
import { useShowSvcPrice } from 'context/ShowSvcPriceContext';

import {
  Box,
  Collapse,
  Flex,
  Grid,
  GridItem,
  Stack,
  Text,
  useTheme
} from '@chakra-ui/react';

import { useAppSelector } from 'store/hooks';

import {
  convertTimeToAMPMFormat,
  formatCurrency,
  getHumanReadableDate,
  groupBy
} from 'utils';

import {
  AppointmentInterface,
  ApptPriceDataInterface,
  DiscountCode
} from 'types';

import { DiscountItem } from 'components';
import { ReservationItem } from './components';

interface ReservationListProps {
  children: ReactNode;
  selectedAppointment: AppointmentInterface;
  onRemoveDiscount: () => void;
  promoData: DiscountCode | null;
  promoCode: string;
  apptPrice: ApptPriceDataInterface | null;
  isInitialOpen: boolean;
}

const ReservationList = ({
  children,
  selectedAppointment,
  promoCode,
  onRemoveDiscount,
  promoData,
  apptPrice,
  isInitialOpen = false
}: ReservationListProps) => {
  const {
    colors: {
      palette: {
        booking: {
          stepReview: { reservationList }
        }
      }
    }
  } = useTheme();

  const showSvcPrice = useShowSvcPrice();
  const { selectedAddons, selectedServices } = useAppSelector(
    (state) => state.bookingAppointment
  );
  const [isOpen, setIsOpen] = useState<boolean>(isInitialOpen);

  const isDiscountApplied = promoData?.is_valid;
  const totalPrice = apptPrice?.price || selectedAppointment.amount_total;
  const { services, start_date, end_time, start_time } = selectedAppointment;
  const servicesById = groupBy(services, 'svc_num');

  const discountPercentValue = promoData?.discount_prc || 0;
  const absoluteDiscountValue = totalPrice
    ? (totalPrice / 100) * discountPercentValue
    : 0;
  const appointmentPrice = absoluteDiscountValue
    ? totalPrice - absoluteDiscountValue
    : totalPrice;

  const toggleAccordion = () => setIsOpen((prevState) => !prevState);

  return (
    <Stack
      borderTopRadius='4px'
      borderBottomRadius='4px'
      overflow='hidden'
      gap={0}
    >
      <Flex flexDirection='column' w='full' onClick={toggleAccordion}>
        <Grid
          position='relative'
          templateColumns='repeat(10, 1fr)'
          px={{ base: 15, md: '30px' }}
          py={{ base: '15px', md: '23px' }}
          columnGap='20px'
          bg={reservationList.background}
        >
          <GridItem colSpan={showSvcPrice ? 7 : 10}>
            <Stack
              h='100%'
              alignItems='start'
              justifyContent='start'
              gap={{ base: '7px', md: '10px' }}
            >
              <Text
                fontSize={{ base: '21px', md: '23px', lg: '25px' }}
                color={reservationList.textColor}
                lineHeight={{ base: '30px', md: '32px', lg: '34px' }}
                fontWeight='bold'
              >
                {getHumanReadableDate(start_date, inputDateFormat)}
                {' at '}
                {convertTimeToAMPMFormat(start_time)}
              </Text>
              <Text
                fontSize={{ base: '12px', md: '14px', lg: '16px' }}
                lineHeight={{ base: '18px', md: '20px', lg: '22px' }}
                color={reservationList.textColor}
                textTransform='uppercase'
              >
                {'Estimated end time '}
                {convertTimeToAMPMFormat(end_time)}
              </Text>
            </Stack>
          </GridItem>

          {showSvcPrice && (
            <GridItem colSpan={3}>
              <Stack
                h='100%'
                alignItems='end'
                justifyContent='center'
                gap={{ base: 0, md: '5px' }}
              >
                <Text
                  fontSize={{ base: '12px', md: '14px' }}
                  color={reservationList.textColor}
                  textTransform='uppercase'
                  textAlign='end'
                  lineHeight={['17px', '19px']}
                >
                  Estimated total
                </Text>
                <Text
                  fontSize={{ base: '25px', md: '27px', lg: '29px' }}
                  color={reservationList.textColor}
                  textTransform='uppercase'
                >
                  {formatCurrency(appointmentPrice < 0 ? 0 : appointmentPrice)}
                </Text>
              </Stack>
            </GridItem>
          )}
        </Grid>
        <Flex
          w='full'
          px={{ base: '15px', md: '30px' }}
          py='14px'
          bg={reservationList.secondaerBackground}
          justifyContent='space-between'
          alignItems='center'
        >
          <Text
            fontSize={{ base: '12px', md: '14px', lg: '16px' }}
            lineHeight={{ base: '18px', md: '20px', lg: '22px' }}
            color={reservationList.textColor}
            textTransform='uppercase'
          >
            Appointment details
          </Text>

          <ChevronDown
            width={29}
            height={29}
            fill={reservationList.iconColor}
            style={{
              transition: 'transform .2s',
              transform: isOpen ? 'scale(-1)' : 'scale(1)'
            }}
          />
        </Flex>
      </Flex>

      <Collapse in={isOpen}>
        {selectedServices.map((service) => {
          const isAddonsExist = selectedAddons[service.id]?.length > 0;
          const dataPrice = (apptPrice?.services ?? []).find(
            (servicePrice) => servicePrice.service_num === service.id
          );
          return (
            <Stack
              gap='5px'
              w='100%'
              key={service.id}
              bg={reservationList.background}
              overflow='hidden'
            >
              <ReservationItem
                item={servicesById[service.id]?.[0]}
                dataPrice={dataPrice}
                showDivider
              />

              {isAddonsExist && (
                <Stack gap='5px'>
                  {selectedAddons[service.id].map((addon) => {
                    const dataAddonPrice = (apptPrice?.services ?? []).find(
                      (servicePrice) => servicePrice.service_num === service.id
                    );
                    return (
                      <ReservationItem
                        key={addon.id}
                        dataPrice={dataAddonPrice}
                        item={servicesById[addon.id][0]}
                        showDivider
                      />
                    );
                  })}
                </Stack>
              )}
            </Stack>
          );
        })}
        {isDiscountApplied && (
          <DiscountItem
            onRemoveDiscount={onRemoveDiscount}
            promoData={promoData}
            promoCode={promoCode}
          />
        )}
        <Box mt='20px'>{children}</Box>
      </Collapse>
    </Stack>
  );
};

export default ReservationList;
